import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { func, array, string } from 'prop-types';

import { TextFieldStyled } from '@nike/nike-design-system-components';

import TextWithBoldSubstr from '../../common/TextWithBoldSubstr';

import { getProductContractGroupsData } from '../selectors';
import { getSelectedAccountId } from '../../../admin/account/selectors';
import { updateProductContractGroup as updateProductContractGroupAction } from '../actions';
import { fetchAdminAccountContracts } from '../../account/actions';
import { SpecializedDropdownInModal } from '../../../styles/styles';
import { sortBy } from '../../../util/lodash';

const CONTAINER_TABINDEX = -22;
const LI_TABINDEX = -12;

const AddContractGroups = ({
  fetchAdminAccountContracts,
  productContractGroups: dropdownOptions,
  selectedAccountId,
  updateProductContractGroup
}) => {
  const [productContractGroupName, setProductContractGroupName] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [suggestions, setSuggestions] = useState(dropdownOptions);

  useEffect(() => {
    const suggestions = productContractGroupName
      ? sortBy(
          dropdownOptions
            .filter(({ accounts }) => !accounts?.includes?.(selectedAccountId))
            .filter(({ name }) => name?.toUpperCase?.()?.includes?.(productContractGroupName.toUpperCase())),
          ({ name }) => name.toLowerCase()
        )
      : dropdownOptions;

    setSuggestions(suggestions);
  }, [productContractGroupName, showDropdown]);

  const handleSubmitProductContractGroups = async productContractGroupName => {
    const addedProductContractGroup = dropdownOptions.find(({ name }) => name === productContractGroupName);

    setProductContractGroupName('');
    setShowDropdown(false);

    await updateProductContractGroup({
      productContractGroupId: addedProductContractGroup.id,
      data: {
        accounts: [...addedProductContractGroup.accounts, selectedAccountId]
      }
    });
    await fetchAdminAccountContracts();
  };

  const handleProductContractGroupName = e => {
    setProductContractGroupName(e.target.value);
  };

  const handleOnBlur = e => {
    if (e?.relatedTarget && e?.relatedTarget.tabIndex === LI_TABINDEX) {
      const {
        name: { value: name }
      } = e.relatedTarget.attributes;

      setProductContractGroupName(name);
      handleSubmitProductContractGroups(name);
    }

    if (e?.relatedTarget?.tabIndex !== CONTAINER_TABINDEX) {
      setShowDropdown(false);
    }
  };

  const handleOnFocus = () => setShowDropdown(true);

  return (
    <>
      <TextFieldStyled
        id='addProductContractGroup'
        name='addProductContractGroup'
        label='Add Product Contract Group'
        value={productContractGroupName}
        onChange={handleProductContractGroupName}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        error={!!productContractGroupName && !suggestions.length}
        errorMessage='No matches found.'
        autoComplete='off'
        style={{ minHeight: 'unset' }}
        inputProps={{ 'data-testid': 'productContractGroupName' }}
        supportTextProps={{ style: { zIndex: 5 } }}
      />
      {!(!showDropdown || (productContractGroupName && !suggestions.length)) && (
        <span tabIndex={`${CONTAINER_TABINDEX}`} onBlur={handleOnBlur}>
          <SpecializedDropdownInModal width='325px' data-testid='specialized-dropdown-pcg'>
            {suggestions.map(({ id, name }) => (
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              <li key={id} tabIndex={`${LI_TABINDEX}`} id={id} name={name}>
                <div>
                  <TextWithBoldSubstr str={name} substr={productContractGroupName} />
                </div>
              </li>
            ))}
          </SpecializedDropdownInModal>
        </span>
      )}
    </>
  );
};

AddContractGroups.propTypes = {
  fetchAdminAccountContracts: func.isRequired,
  productContractGroups: array.isRequired,
  selectedAccountId: string.isRequired,
  updateProductContractGroup: func.isRequired
};

export default connect(
  state => {
    const selectedAccountId = getSelectedAccountId(state);

    return {
      productContractGroups: getProductContractGroupsData(state).filter(
        ({ accounts }) => !accounts?.includes?.(selectedAccountId)
      ),
      selectedAccountId
    };
  },
  { fetchAdminAccountContracts, updateProductContractGroup: updateProductContractGroupAction }
)(AddContractGroups);
